import React from 'react';
import { Product, ProductType, PocketAssignment } from '../types';
import { TrashIcon, PlusCircleIcon } from '../constants';
import SingleProductSelectionInput from './SingleProductSelectionInput';

interface PocketAssignmentInputProps {
  allProducts: Product[];
  pocketAssignments: PocketAssignment[];
  onChange: (newPocketAssignments: PocketAssignment[]) => void;
  error?: string;
  maxPockets?: number;
}

const PocketAssignmentInput: React.FC<PocketAssignmentInputProps> = ({
  allProducts,
  pocketAssignments,
  onChange,
  error,
  maxPockets = 4,
}) => {
  const addPocket = () => {
    if (pocketAssignments.length < maxPockets) {
      const nextPocketNumber = Math.max(0, ...pocketAssignments.map(p => p.pocketNumber)) + 1;
      const newAssignments = [...pocketAssignments, { pocketNumber: nextPocketNumber, productId: '' }];
      onChange(newAssignments);
    }
  };

  const removePocket = (pocketNumber: number) => {
    const newAssignments = pocketAssignments.filter(p => p.pocketNumber !== pocketNumber);
    onChange(newAssignments);
  };

  const updatePocketProduct = (pocketNumber: number, productId: string) => {
    const newAssignments = pocketAssignments.map(p =>
      p.pocketNumber === pocketNumber ? { pocketNumber: p.pocketNumber, productId } : p
    );
    onChange(newAssignments);
  };


  const sortedAssignments = [...pocketAssignments].sort((a, b) => a.pocketNumber - b.pocketNumber);

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-3">
        <label className="block text-sm font-medium text-slate-700">
          Pocket Assignments ({pocketAssignments.length}/{maxPockets})
          <span className="text-red-500"> *</span>
        </label>
        {pocketAssignments.length < maxPockets && (
          <button
            type="button"
            onClick={addPocket}
            className="flex items-center px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Add Pocket
          </button>
        )}
      </div>

      {sortedAssignments.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
          <p className="text-slate-500 text-sm">No pockets assigned yet</p>
          <button
            type="button"
            onClick={addPocket}
            className="mt-2 inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            <PlusCircleIcon className="w-4 h-4 mr-1" />
            Add First Pocket
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedAssignments.map((assignment) => {
            return (
              <div key={`pocket-assignment-${assignment.pocketNumber}`} className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                <div className="flex-shrink-0 mt-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 text-sm font-medium text-white bg-indigo-600 rounded-full">
                    {assignment.pocketNumber}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <SingleProductSelectionInput
                    key={`single-product-selection-${assignment.pocketNumber}`}
                    allProducts={allProducts}
                    selectedProductId={assignment.productId}
                    onChange={(productId) => updatePocketProduct(assignment.pocketNumber, productId)}
                    label={`Pocket ${assignment.pocketNumber} Product`}
                    inputId={`pocket-${assignment.pocketNumber}-product`}
                    productType={ProductType.OUTFEED}
                    required
                  />
                </div>
                <div className="flex-shrink-0 mt-6">
                  <button
                    type="button"
                    onClick={() => removePocket(assignment.pocketNumber)}
                    className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-md transition-colors"
                    title={`Remove pocket ${assignment.pocketNumber}`}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      
      <p className="mt-2 text-xs text-slate-500">
        Each pocket must have an outfeed product assigned. The same product can be assigned to multiple pockets. You can create up to {maxPockets} pockets.
      </p>
    </div>
  );
};

export default PocketAssignmentInput;